// Strava: the profile's Connected apps row (connect, "post new tracks automatically", disconnect) and a Track's
// "Send to Strava" (or, until Strava can be linked here, its GPX to upload on Strava by hand).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '@/api/client';
import { useFeature } from '@/auth/me';
import { openSubscribe } from '@/features/billing/billing';
import { PremiumBadge } from '@/features/billing/Premium';
import type { ConnectionError } from './connections';
import {
  STRAVA_UPLOAD_URL,
  stravaActivityUrl,
  useConnect,
  useConnection,
  useDisconnect,
  useSetAutoUpload,
  useStravaStatus,
  useUploadToStrava,
} from './connections';
import garminStyles from '@/features/garmin/Garmin.module.css';
import styles from './Connections.module.css';

/** Profile → Connected apps: Strava. */
export function StravaConnection() {
  const { t } = useTranslation();
  const connection = useConnection('strava');
  const premium = useFeature('strava');
  const connect = useConnect('strava');
  const disconnect = useDisconnect('strava');
  const autoUpload = useSetAutoUpload('strava');
  const c = connection.data;
  if (!c) return null;
  const linked = c.connected && !c.needsRelink;
  const busy = connect.isPending || disconnect.isPending;
  const error = connect.error ?? disconnect.error ?? autoUpload.error;
  const startConnect = () =>
    premium ? connect.mutate() : openSubscribe({ feature: 'strava', then: () => connect.mutate() });

  return (
    <div className={styles.provider}>
      <div className={garminStyles.appRow}>
        <span className={garminStyles.appName}>
          <span className={styles.stravaName}>Strava</span>
          <span className={`${garminStyles.state} ${linked ? garminStyles.stateOn : ''}`}>
            {!c.available
              ? t('connections.unavailable')
              : c.needsRelink
                ? t('connections.needsRelink')
                : linked
                  ? c.displayName
                    ? t('connections.connectedAs', { name: c.displayName })
                    : t('garmin.connected')
                  : t('garmin.notConnected')}
          </span>
        </span>
        {c.available &&
          (linked ? (
            <button
              type="button"
              className={garminStyles.button}
              disabled={busy}
              onClick={() => disconnect.mutate()}
            >
              {disconnect.isPending ? t('garmin.disconnecting') : t('garmin.disconnect')}
            </button>
          ) : (
            // Strava's own "Connect with Strava" look, as its brand guidelines ask.
            <button type="button" className={styles.stravaButton} disabled={busy} onClick={startConnect}>
              {connect.isPending ? t('garmin.connecting') : t('connections.strava.connect')}
              {!premium && <PremiumBadge />}
            </button>
          ))}
      </div>
      {linked && (
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={autoUpload.isPending ? autoUpload.variables : c.autoUpload}
            disabled={autoUpload.isPending}
            onChange={(event) => autoUpload.mutate(event.target.checked)}
          />
          <span>
            {t('connections.strava.autoUpload')}
            <small>{t('connections.strava.autoUploadHint')}</small>
          </span>
        </label>
      )}
      {!c.available && <p className={garminStyles.note}>{t('connections.strava.byHand')}</p>}
      {error && (
        <p role="alert" className={garminStyles.error}>
          {errorText(t, error)}
        </p>
      )}
      {linked && <p className={styles.poweredBy}>{t('connections.strava.poweredBy')}</p>}
    </div>
  );
}

/**
 * A Track's Properties: send it to Strava (once; then a link to it there). Until Strava can be linked here, its
 * GPX to download and upload on Strava's own page.
 */
export function SendToStrava({ trackGuid }: { trackGuid: string }) {
  const { t } = useTranslation();
  const connection = useConnection('strava');
  const c = connection.data;
  const linked = c?.connected === true && !c.needsRelink;
  const status = useStravaStatus(trackGuid, linked);
  const upload = useUploadToStrava(trackGuid);
  if (!c) return null;
  if (!c.available) return <UploadByHand trackGuid={trackGuid} />;
  if (!linked) return null;

  const sent = upload.data ?? status.data;
  if (sent && !sent.error) {
    return (
      <p role="status" className={`${garminStyles.success} ${styles.send}`}>
        {sent.activityId ? (
          <a href={stravaActivityUrl(sent.activityId)} target="_blank" rel="noreferrer">
            {t('connections.strava.sentView')}
          </a>
        ) : (
          t('connections.strava.sentProcessing')
        )}
      </p>
    );
  }
  return (
    <div className={styles.send}>
      <button
        type="button"
        className={styles.stravaButton}
        disabled={upload.isPending || status.isPending}
        onClick={() => upload.mutate()}
      >
        {upload.isPending ? t('connections.strava.sending') : t('connections.strava.send')}
      </button>
      {sent?.error && (
        <p role="alert" className={garminStyles.error}>
          {t('connections.strava.refused', { reason: sent.error })}
        </p>
      )}
      {upload.error && (
        <p role="alert" className={garminStyles.error}>
          {errorText(t, upload.error)}
        </p>
      )}
    </div>
  );
}

/** Strava can't be linked here yet: the track's GPX, and Strava's page to upload it on. */
function UploadByHand({ trackGuid }: { trackGuid: string }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const download = async () => {
    setFailed(false);
    try {
      const response = await apiFetch('/data/exportGuids?exportType=0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([trackGuid]),
      });
      if (!response.ok) throw new Error(`Export: ${response.status}`);
      const name = decodeURIComponent(response.headers.get('X-Filename') ?? 'track.gpx');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(await response.blob());
      link.download = name;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      setFailed(true);
    }
  };
  return (
    <div className={styles.send}>
      <div className={styles.actions}>
        <button type="button" className={garminStyles.button} onClick={() => void download()}>
          {t('connections.strava.downloadGpx')}
        </button>
        <a className={styles.stravaButton} href={STRAVA_UPLOAD_URL} target="_blank" rel="noreferrer">
          {t('connections.strava.uploadOnStrava')}
        </a>
      </div>
      <p className={garminStyles.note}>{t('connections.strava.byHandHint')}</p>
      {failed && (
        <p role="alert" className={garminStyles.error}>
          {t('connections.errors.download')}
        </p>
      )}
    </div>
  );
}

function errorText(t: (key: string, options?: Record<string, unknown>) => string, error: ConnectionError) {
  switch (error.code) {
    case '1050':
      return t('connections.premium');
    case '1057':
      return t('connections.errors.notConnected');
    case '1058':
      return t('connections.errors.expired');
    case '1059':
      return t('connections.unavailable');
    case '1054':
      return t('garmin.errors.tooShort');
    default:
      return t('connections.errors.failed');
  }
}
